
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HotelLayout from '@/components/Hotels/HotelLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Star,
  MapPin,
  ExternalLink,
  Coffee,
  Wifi,
  CheckCircle,
  Calendar,
  Users,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getHotelById, getHotelPrices } from '@/utils/hotels/mockData';

const HotelDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const hotel = getHotelById(id || '');
  const prices = getHotelPrices(id || '');
  
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  
  if (!hotel) {
    return (
      <HotelLayout>
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold mb-4">Hotel not found</h1>
          <p className="mb-8">We couldn't find the hotel you're looking for.</p>
          <Button onClick={() => navigate('/hotels')}>Back to Hotels</Button>
        </div>
      </HotelLayout>
    );
  }
  
  const nextImage = () => {
    setActiveImageIndex((prev) => (prev + 1) % hotel.images.length);
  };
  
  const prevImage = () => {
    setActiveImageIndex((prev) => (prev === 0 ? hotel.images.length - 1 : prev - 1));
  };
  
  return (
    <HotelLayout>
      <div className="container mx-auto px-4 py-6">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-2">
            <ChevronLeft size={16} className="mr-1" /> Back to search results
          </Button>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">{hotel.name}</h1>
              <div className="flex items-center mt-1">
                <div className="flex mr-2">
                  {Array(hotel.stars).fill(0).map((_, i) => (
                    <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <div className="flex items-center text-sm text-muted-foreground">
                  <MapPin size={14} className="mr-1" />
                  <span>{hotel.location}, {hotel.city}, {hotel.country}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <div className="bg-primary/10 text-primary font-semibold rounded-full px-3 py-1">
                {hotel.rating}
              </div>
              <span className="ml-2 text-sm text-muted-foreground">
                ({hotel.reviewCount} reviews)
              </span>
            </div>
          </div>
        </div>
        
        {/* Hotel images */}
        <div className="relative h-[300px] md:h-[400px] lg:h-[500px] overflow-hidden rounded-lg mb-8">
          <img 
            src={hotel.images[activeImageIndex]} 
            alt={hotel.name} 
            className="w-full h-full object-cover" 
          />
          <Button 
            variant="outline" 
            size="icon" 
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/80 hover:bg-white"
            onClick={prevImage}
          >
            <ChevronLeft size={20} />
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/80 hover:bg-white"
            onClick={nextImage}
          >
            <ChevronRight size={20} />
          </Button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center space-x-2">
            {hotel.images.map((_, index) => (
              <button 
                key={index} 
                className={cn(
                  "w-2 h-2 rounded-full",
                  index === activeImageIndex ? "bg-white" : "bg-white/50"
                )}
                onClick={() => setActiveImageIndex(index)}
              />
            ))}
          </div>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Hotel details */}
          <div className="lg:w-2/3 space-y-6">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-4">About this hotel</h2>
                <p className="mb-6">{hotel.description}</p>
                
                <h3 className="text-lg font-medium mb-3">Amenities</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2">
                  {hotel.amenities.map((amenity, i) => (
                    <div key={i} className="flex items-center">
                      <CheckCircle size={16} className="mr-2 text-green-600" /> {amenity}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-4">Location</h2>
                <div className="h-[300px] bg-muted rounded mb-4 flex items-center justify-center">
                  <p className="text-sm text-muted-foreground">Map would be displayed here</p>
                </div>
                <div className="flex items-start">
                  <MapPin className="mr-2 mt-1 text-primary" />
                  <p>{hotel.location}, {hotel.city}, {hotel.country}</p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Prices and booking */}
          <div className="lg:w-1/3">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-4">Available offers</h2>
                <div className="space-y-4">
                  {prices.map((price, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <img 
                            src={price.providerLogo}
                            alt={price.provider} 
                            className="h-8 mr-2 object-contain" 
                          />
                          <span className="font-medium">{price.provider}</span>
                        </div>
                        {price.discount && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Save {price.discount}%</Badge>
                        )}
                      </div>
                      
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm">{price.roomType}</span>
                        <div className="text-right">
                          {price.originalPrice && (
                            <span className="text-sm line-through text-muted-foreground mr-2">
                              ${price.originalPrice}
                            </span>
                          )}
                          <span className="font-bold text-lg">${price.price}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-y-1 mb-3">
                        <div className="flex items-center text-xs mr-3">
                          <CheckCircle size={12} className="mr-1 text-green-600" /> {price.cancellationPolicy}
                        </div>
                        {price.breakfast && (
                          <div className="flex items-center text-xs mr-3">
                            <Coffee size={12} className="mr-1 text-green-600" /> Breakfast
                          </div>
                        )}
                        {price.wifi && (
                          <div className="flex items-center text-xs">
                            <Wifi size={12} className="mr-1 text-green-600" /> Free WiFi
                          </div>
                        )}
                      </div>
                      
                      <Button className="w-full flex items-center justify-center" variant="default">
                        View Deal <ExternalLink size={14} className="ml-2" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                <div className="text-center mt-4">
                  <p className="text-xs text-muted-foreground">Prices shown are per night and may not include all taxes and fees.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </HotelLayout>
  );
};

export default HotelDetail;
